import { Row, Table } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import {
    IHlasovaniHromadne,
    IHlasovaniHromadneBody,
    IHromadneHlasovaniData,
    IHromadneHlasovaniUcty,
    IHromadneHlasovaniUctyInput,
    hromadneHlasovaniPocetPut,
    hromadneHlasovaniUctyGET
} from 'services/spravceUctu/hromadneHlasovani';

import { ENotificationType } from 'enums/notificationType';

import HlasovaniHromadne from './HlasovaniHromadne';

interface HromadneHlasovaniTableProps {
    page: number;
    bodyHlasovani: IHlasovaniHromadneBody[];
    zapsaneHlasy: IHlasovaniHromadne[];
    updateHlasovani: (v: IHlasovaniHromadne) => void;
    setConfirmContinue: React.Dispatch<React.SetStateAction<boolean[] | undefined>>;
    submiting: boolean;
    setAllowToPut: React.Dispatch<React.SetStateAction<boolean>>;
    setVisible: React.Dispatch<React.SetStateAction<boolean>>;
    setActiveModal: React.Dispatch<React.SetStateAction<number>>;
    akcionar: IHromadneHlasovaniData;
    IDhlasovani: string | number;
}

const HromadneHlasovaniTable: React.FC<HromadneHlasovaniTableProps> = ({
    page,
    bodyHlasovani,
    updateHlasovani,
    setConfirmContinue,
    submiting,
    setAllowToPut,
    setVisible,
    setActiveModal,
    akcionar,
    IDhlasovani,
    zapsaneHlasy
}) => {
    const { NotificationComponent } = useContext(Context);
    const [loading, setLoading] = useState<boolean>(true);
    const [refetch, setRefetch] = useState<boolean>(true);
    const [pagination, setPagination] = useState<IHromadneHlasovaniUctyInput>({
        order: 'oid:desc',
        limit: 10,
        offset: 0
    });
    const [selectedUcty, setSelectedUcty] = useState<IHromadneHlasovaniUcty>();

    useEffect(() => {
        setLoading(false);
        hromadneHlasovaniUctyGET(IDhlasovani, {
            ...pagination,
            vybran: true
        })
            .then(data => setSelectedUcty(data))
            .finally(() => setLoading(false));
    }, [refetch, pagination, IDhlasovani]);

    return (
        <>
            <LoadingWrapper loading={loading}>
                {page > 1 && (
                    <Table
                        loading={loading}
                        rowKey={(_, index) => index || 0}
                        pagination={{
                            total: selectedUcty?.count,
                            onChange: tablePage =>
                                setPagination(pv => {
                                    return {
                                        ...pv,
                                        offset: (tablePage - 1) * 10
                                    };
                                }),
                            current: (pagination.offset || 0) / 10 + 1,
                            showSizeChanger: false
                        }}
                        locale={{
                            emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                        }}
                        style={{
                            marginBottom: '60px'
                        }}
                        columns={[
                            {
                                title: <TranslateText>AKCIONAR_PROFIL_MAJETKOVY_UCET</TranslateText>,
                                dataIndex: 'ucet',
                                key: 'ucet'
                            },
                            {
                                title: <TranslateText>MAJITEL</TranslateText>,
                                dataIndex: 'nazev',
                                key: 'nazev'
                            },
                            {
                                title: <TranslateText>SRD_HLASOVANI_MAX_POC_HLASU</TranslateText>,
                                dataIndex: 'pocet',
                                key: 'pocet',
                                render: (pocet, row) => {
                                    return pocet + '/' + row.max;
                                }
                            },
                            ...(page === 2
                                ? [
                                      {
                                          title: <TranslateText>AKCIONAR_HLASOVANI_MAXPOCET</TranslateText>,
                                          dataIndex: 'ucet',
                                          key: 'ucet',
                                          width: 220,
                                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                          render: (text: string, row: any, index: number) => (
                                              <InputNumberComponent
                                                  name={text}
                                                  style={{
                                                      position: 'relative',
                                                      height: '20px'
                                                  }}
                                                  precision={0}
                                                  notRequired={true}
                                                  id={row.oid}
                                                  initialValue={row.pocet}
                                                  value={row.pocet}
                                                  onChangeCustom={val =>
                                                      setSelectedUcty(pv => {
                                                          const newState = [...(pv?.uctyudalosti || [])];
                                                          if (
                                                              newState[index].max < (val as number) ||
                                                              (val as number) < 0
                                                          ) {
                                                              NotificationComponent({
                                                                  title: (
                                                                      <TranslateText>
                                                                          NEVALIDNI_POCET_HLASU
                                                                      </TranslateText>
                                                                  ),
                                                                  type: ENotificationType.ERROR
                                                              });
                                                              return pv;
                                                          }
                                                          newState[index].pocet = typeof val === 'number' ? val : 0;
                                                          return {
                                                              ...pv!,
                                                              uctyudalosti: newState
                                                          };
                                                      })
                                                  }
                                                  onBlur={ev => {
                                                      setRefetch(pv => !pv);
                                                      hromadneHlasovaniPocetPut(
                                                          IDhlasovani,
                                                          row.oid,
                                                          String(
                                                              ev.target.value || (ev.target.value === '0' ? 0 : row.max)
                                                          )
                                                              .split(' ')
                                                              .join('')
                                                      );
                                                  }}
                                              />
                                          )
                                      }
                                  ]
                                : [])
                        ]}
                        dataSource={selectedUcty?.uctyudalosti}
                    />
                )}
                {bodyHlasovani.map((body, index) => (
                    <div key={index + body.issrlabl}>
                        <Row
                            style={
                                body.forinfonly && {
                                    marginBottom: '24px'
                                }
                            }
                        >
                            <AnchorComponent
                                link={true}
                                key={index}
                                onClick={() => {
                                    setConfirmContinue(pv => pv?.map(() => false));
                                    setVisible(val => !val);
                                    setActiveModal(index);
                                }}
                            >
                                {body.issrlabl + ' ' + body.titl}
                            </AnchorComponent>
                        </Row>
                        {!body.forinfonly && (
                            <HlasovaniHromadne
                                body={body}
                                page={page}
                                updateHlasovani={updateHlasovani}
                                zapsanyHlas={
                                    zapsaneHlasy?.filter(e => e.issrlabl === akcionar.vh.bodyvh[index].issrlabl)[0]
                                }
                                bodyHlasovani={
                                    akcionar?.hlasovani?.sezhlasovani?.filter(
                                        e => e.issrlabl === akcionar.vh.bodyvh[index].issrlabl
                                    )[0] || []
                                }
                                indexBody={index}
                                setConfirmContinue={setConfirmContinue}
                                submiting={submiting}
                                setAllowToPut={setAllowToPut}
                            />
                        )}
                    </div>
                ))}
            </LoadingWrapper>
        </>
    );
};

export default HromadneHlasovaniTable;
